import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Copy, Send, Trash2, RefreshCw, Save, X, Search, Mail, ShieldCheck, Eye, EyeOff, ChevronDown, ChevronRight, User as UserIcon, MapPin } from "lucide-react";
import { fmtDate } from "../lib/formatters";

// Founder-only page: audit participants' portal logins in one place, copy their
// email address, and either generate a random password or set a specific one.
// Mounted at /participant-logins under the "Management" cluster of the sidebar.
export default function ParticipantLogins() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [modal, setModal] = useState({ open: false, participant: null }); // set-password modal
  const [manualPw, setManualPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState("");
  const [reveal, setReveal] = useState({ open: false, email: "", password: "" });
  const [expanded, setExpanded] = useState({});  // participant_id -> {loading, info}

  const toggleExpand = async (p) => {
    if (expanded[p.id]) {
      setExpanded(e => ({ ...e, [p.id]: undefined }));
      return;
    }
    setExpanded(e => ({ ...e, [p.id]: { loading: true } }));
    try {
      const { data } = await api.get(`/participants/${p.id}/general-info`);
      setExpanded(e => ({ ...e, [p.id]: { loading: false, info: data || {} } }));
    } catch {
      setExpanded(e => ({ ...e, [p.id]: { loading: false, info: {} } }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/participants");
      setRows(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return (r.name || "").toLowerCase().includes(q)
      || (r.email || "").toLowerCase().includes(q)
      || (r.booking?.programme || "").toLowerCase().includes(q);
  });

  const copyEmail = async (email) => {
    try { await navigator.clipboard.writeText(email); toast.success("Email copied"); }
    catch { toast.error("Clipboard blocked — copy manually"); }
  };

  const randomReset = async (p) => {
    if (!p.participant_booking_id) return;
    if (!window.confirm(`Generate a new random password for ${p.name}?`)) return;
    setBusy(`r-${p.id}`);
    try {
      const { data } = await api.post(`/participants/${p.participant_booking_id}/send-credentials`, { send_email: false });
      setReveal({ open: true, email: data.email, password: data.password });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const emailReset = async (p) => {
    if (!p.participant_booking_id) return;
    setBusy(`e-${p.id}`);
    try {
      const { data } = await api.post(`/participants/${p.participant_booking_id}/send-credentials`, { send_email: true });
      try { await navigator.clipboard.writeText(`Login: ${data.email}\nPassword: ${data.password}`); }
      catch (err) { console.debug("clipboard write failed:", err?.message); }
      toast.success("Credentials emailed · copied to clipboard");
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const openManual = (p) => { setManualPw(""); setShowPw(false); setModal({ open: true, participant: p }); };
  const closeManual = () => setModal({ open: false, participant: null });

  const saveManual = async () => {
    if (!modal.participant) return;
    if (manualPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy("m");
    try {
      await api.post(`/participants/${modal.participant.participant_booking_id}/set-password`, { password: manualPw });
      toast.success(`New password set for ${modal.participant.name}`);
      closeManual();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const revoke = async (p) => {
    if (!window.confirm(`Revoke portal access for ${p.name}? Their booking, C-form and service requests are kept, but they won't be able to sign in.`)) return;
    setBusy(`d-${p.id}`);
    try {
      await api.delete(`/participants/${p.participant_booking_id}`);
      toast.success(`${p.name} — portal access revoked`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  const copyReveal = async () => {
    try {
      await navigator.clipboard.writeText(`Login: ${reveal.email}\nPassword: ${reveal.password}`);
      toast.success("Credentials copied");
    } catch { toast.error("Clipboard blocked"); }
  };

  return (
    <div className="space-y-6" data-testid="participant-logins-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Management</div>
        <h1 className="text-2xl font-display font-semibold">Participant logins</h1>
        <p className="text-sm text-[color:var(--aii-muted)] mt-1">Every participant who has portal access. Copy their email, set a specific password, or reset it to a random one.</p>
      </div>

      <div className="aii-card p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[240px] bg-slate-50 border border-[color:var(--aii-border)] rounded-md px-3 py-2">
            <Search size={14} className="text-[color:var(--aii-muted)]"/>
            <input data-testid="pl-search" value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Search by name, email or programme…"
              className="flex-1 bg-transparent text-sm focus:outline-none"/>
          </div>
          <button onClick={load} data-testid="pl-refresh" className="text-xs px-3 py-2 border border-[color:var(--aii-border)] rounded-md hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1.5"><RefreshCw size={12}/> Refresh</button>
          <div className="text-xs text-[color:var(--aii-muted)]">{filtered.length} of {rows.length}</div>
        </div>
      </div>

      <div className="aii-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-[color:var(--aii-muted)]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <KeyRound size={28} className="mx-auto text-[color:var(--aii-muted)] opacity-40 mb-2"/>
            <div className="text-sm text-[color:var(--aii-muted)]">{rows.length === 0 ? "No participants have portal access yet. Provision one from Program Bookings." : "No participants match your search."}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="aii-table" style={{ minWidth: 720 }}>
              <thead>
                <tr><th>Name</th><th>Login email</th><th>Programme</th><th>Dates</th><th className="text-right">Actions</th></tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const ex = expanded[p.id];
                  const infoKeys = ex?.info ? Object.entries(ex.info).filter(([k, v]) => v && !["participant_user_id", "created_at", "updated_at"].includes(k)) : [];
                  return (
                    <>
                      <tr key={p.id} data-testid={`pl-row-${p.id}`}>
                        <td>
                          <div className="flex items-center gap-2">
                            <button onClick={() => toggleExpand(p)} className="text-[color:var(--aii-muted)] hover:text-brand p-0.5" data-testid={`pl-expand-${p.id}`}
                              title="View general info">
                              {ex ? <ChevronDown size={13}/> : <ChevronRight size={13}/>}
                            </button>
                            <div className="w-8 h-8 rounded-full bg-[color:var(--aii-green-tint)] text-[color:var(--aii-green-dark)] font-bold flex items-center justify-center font-display text-sm">{(p.name || "?")[0]}</div>
                            <div className="font-medium">{p.name}</div>
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-mono">{p.email}</span>
                            <button onClick={() => copyEmail(p.email)} data-testid={`pl-copy-email-${p.id}`} title="Copy email" className="text-[color:var(--aii-muted)] hover:text-brand p-1"><Copy size={11}/></button>
                          </div>
                        </td>
                        <td className="text-xs">
                          {p.booking?.programme || <em className="text-[color:var(--aii-muted)]">no booking</em>}
                          {p.stay?.city && (
                            <div className="text-[10px] text-brand mt-0.5 flex items-center gap-1" data-testid={`pl-stay-${p.id}`} title={p.stay.address || ""}>
                              <MapPin size={9}/>
                              <span className="truncate max-w-[180px]">{p.stay.property_name || p.stay.room} · {p.stay.city}</span>
                            </div>
                          )}
                        </td>
                        <td className="text-[11px] text-[color:var(--aii-muted)]">{fmtDate(p.booking?.start_date) || "?"} → {fmtDate(p.booking?.end_date) || "?"}</td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            <button onClick={() => openManual(p)} data-testid={`pl-set-pw-${p.id}`} title="Set a specific password"
                              className="text-xs px-2 py-1 border border-brand text-brand rounded hover:bg-[color:var(--aii-green-tint)] flex items-center gap-1">
                              <KeyRound size={11}/> Set
                            </button>
                            <button onClick={() => randomReset(p)} disabled={busy === `r-${p.id}`} data-testid={`pl-random-${p.id}`} title="Generate a random password"
                              className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
                              <RefreshCw size={11}/> Random
                            </button>
                            <button onClick={() => emailReset(p)} disabled={busy === `e-${p.id}`} data-testid={`pl-email-${p.id}`} title="Reset + email credentials"
                              className="text-xs px-2 py-1 border border-[color:var(--aii-border)] rounded hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
                              <Send size={11}/> Email
                            </button>
                            <button onClick={() => revoke(p)} disabled={busy === `d-${p.id}`} data-testid={`pl-revoke-${p.id}`} title="Revoke portal access"
                              className="text-[color:var(--aii-muted)] hover:text-red-600 p-1.5 disabled:opacity-50">
                              <Trash2 size={12}/>
                            </button>
                          </div>
                        </td>
                      </tr>
                      {ex && (
                        <tr data-testid={`pl-info-${p.id}`} className="bg-[color:var(--aii-bg)]">
                          <td colSpan={5} className="px-4 py-3">
                            {ex.loading ? (
                              <div className="text-xs text-[color:var(--aii-muted)]">Loading general info…</div>
                            ) : infoKeys.length === 0 ? (
                              <div className="text-xs text-[color:var(--aii-muted)] italic flex items-center gap-1.5">
                                <UserIcon size={11}/> No general info submitted yet by this participant.
                              </div>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
                                {infoKeys.map(([k, v]) => (
                                  <div key={k} className="flex flex-col">
                                    <span className="text-[10px] uppercase tracking-widest text-[color:var(--aii-muted)]">{k.replace(/_/g, " ")}</span>
                                    <span className="text-[color:var(--aii-text-2)]">{String(v)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Set specific password modal */}
      {modal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl" data-testid="pl-set-pw-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Set password</div>
                <h3 className="font-display text-lg font-semibold">{modal.participant?.name}</h3>
                <div className="text-xs text-[color:var(--aii-muted)] font-mono mt-0.5">{modal.participant?.email}</div>
              </div>
              <button onClick={closeManual}><X size={18}/></button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">New password (min 6 chars)</label>
              <div className="mt-1 flex items-center gap-2 border border-[color:var(--aii-border)] rounded-md bg-white px-3 py-2">
                <ShieldCheck size={14} className="text-brand"/>
                <input data-testid="pl-manual-pw" type={showPw ? "text" : "password"} autoFocus autoComplete="new-password"
                  value={manualPw} onChange={(e) => setManualPw(e.target.value)}
                  className="flex-1 bg-transparent text-sm focus:outline-none font-mono"
                  placeholder="e.g. Rooftop2026"/>
                <button type="button" onClick={() => setShowPw(!showPw)} className="text-[color:var(--aii-muted)] hover:text-brand">
                  {showPw ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
              <p className="text-[11px] text-[color:var(--aii-muted)] mt-2">The participant can change it themselves later from <b>Profile → Password</b>.</p>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeManual} className="px-4 py-2 text-sm">Cancel</button>
              <button data-testid="pl-manual-save" onClick={saveManual} disabled={busy === "m" || manualPw.length < 6}
                className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark disabled:opacity-50 flex items-center gap-1.5">
                <Save size={14}/> {busy === "m" ? "Saving…" : "Save password"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reveal random password modal */}
      {reveal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl text-center" data-testid="pl-reveal-modal">
            <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3"><KeyRound size={22}/></div>
            <h3 className="font-display text-lg font-semibold">New password ready</h3>
            <p className="text-xs text-[color:var(--aii-muted)] mt-1">Copy it now — you won&rsquo;t see it again. Share it with the participant securely.</p>
            <div className="mt-4 bg-slate-50 border border-[color:var(--aii-border)] rounded-md p-4 text-left space-y-2 font-mono text-sm">
              <div className="flex items-center gap-2 text-xs text-[color:var(--aii-muted)]"><Mail size={11}/> {reveal.email}</div>
              <div className="text-lg font-bold tracking-widest text-brand text-center py-1">{reveal.password}</div>
            </div>
            <div className="flex justify-center gap-2 mt-5">
              <button onClick={copyReveal} data-testid="pl-reveal-copy" className="bg-brand text-white font-semibold px-4 py-2 rounded-md hover:bg-brand-dark flex items-center gap-1.5"><Copy size={13}/> Copy</button>
              <button onClick={() => setReveal({ open: false, email: "", password: "" })} data-testid="pl-reveal-close" className="border border-[color:var(--aii-border)] px-4 py-2 rounded-md">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
